import { RESPONSE_MESSAGES } from "@/constants/components";
import { CONSENT_KEY, INDEX_DB, ONBOARDING_DONE } from "@/constants/general";
import { routes } from "@/constants/routes";
import { ONBOARDING_NAV_PAGES } from "@/constants/welcome-intro";
import {
  redirectTo,
  setItemWithExpiration,
  setToLocalStorage,
} from "@/core/utils/utils.helper";
import {
  iDbClearTable,
  iDbGetAllRecords,
  iDbGetRecordsByField,
  iDbUpsertRecord,
} from "@/index-db/db.helpers";
import {
  MenopauseAssessmentDataType,
  OnboardingProgressData,
  OurConditionsConsentDataType,
  UserConsentDataType,
  UserDataForQuizType,
} from "@/index-db/db.types";
import { saveUserSpecificConsent } from "@/services/consent.service";
import { saveConditionsAccept } from "@/services/ourConditions.service";
import { submitQuizResponse } from "@/services/quiz.service";
import { Location, NavigateFunction } from "react-router-dom";
import { responseMessage } from "./errorHandling.helper";

const ONBOARDING_VALUE_KEY = "userProgress";

/** Get onboarding progress from index db */
export const getOnboardingProgress = async () => {
  return await iDbGetRecordsByField<OnboardingProgressData>(
    INDEX_DB.ONBOARDING_PROGRESS_TABLE,
    "key",
    ONBOARDING_VALUE_KEY,
  );
};

/** Save onboarding progress to index db
 * @param state - Onboarding progress data
 */
export const saveOnboardingProgress = async (state: OnboardingProgressData) => {
  return await iDbUpsertRecord(INDEX_DB.ONBOARDING_PROGRESS_TABLE, {
    key: ONBOARDING_VALUE_KEY,
    ...state,
  });
};

export const setOnboardingComplete = async () => {
  setToLocalStorage(ONBOARDING_DONE, "true");
};

/** Handle navigation to previous onboarding page along with index db update
 * @param location - Location object from react-router-dom
 * @param navigate - Navigate object from react-router-dom
 * @returns Boolean indicating if navigation has already been done or not
 */
export const handleOnboardingBackNavigation = (
  location: Location<any>,
  navigate: NavigateFunction,
) => {
  // Onboarding pages in the sequential order of their appearance in onboarding flow
  const onboardingPages = [
    { route: "/", pageName: ONBOARDING_NAV_PAGES.WELCOME },
    { route: `/${routes.intro.path}`, pageName: ONBOARDING_NAV_PAGES.INTRO },
    {
      route: `${routes.home.path}/${routes.tapToStart.path}`,
      pageName: ONBOARDING_NAV_PAGES.TAPTOSTART,
    },
  ];
  // Get index of current pathname in the onboarding pages array
  const currPageIndex = onboardingPages.findIndex(
    (page) => page.route === location.pathname,
  );
  // Navigation is required if current pathname is 2nd object or above
  const shouldNavigate = currPageIndex > 0;
  if (shouldNavigate) {
    // Update index db
    saveOnboardingProgress({
      currentPage: onboardingPages[currPageIndex - 1].pageName,
      introSlideIndex: 0,
    });
    // Navigate to previous page
    navigate(onboardingPages[currPageIndex - 1].route);
  }
  // Return indication of navigation already been done or not
  return shouldNavigate;
};

/** Save user consent data if available */
export const submitLocalConsentData = async () => {
  const [userConsentData] = await iDbGetAllRecords<UserConsentDataType>(
    INDEX_DB.CONSENTS_TABLE,
  );
  if (!userConsentData) return;

  const userConsentResponse = await saveUserSpecificConsent(userConsentData);
  try {
    if (userConsentResponse?.status === 200) {
      const oneDayInMilliseconds = 24 * 60 * 60 * 1000; // 24 hour
      setItemWithExpiration(CONSENT_KEY, "true", oneDayInMilliseconds);
      iDbClearTable(INDEX_DB.CONSENTS_TABLE);
    } else {
      responseMessage(RESPONSE_MESSAGES.ERROR);
    }
  } catch (error: any) {
    console.error(
      "An error occurred while submitting local consent data:",
      error,
    );
    throw error;
  }
};

/** Save our conditions consent data if available in IndexedDB
 * @param userId - The user ID to use for the API call
 */
export const submitLocalOurConditionsData = async (userId: string) => {
  const [ourConditionsData] =
    await iDbGetAllRecords<OurConditionsConsentDataType>(
      INDEX_DB.OUR_CONDITIONS_TABLE,
    );
  if (!ourConditionsData) return;

  const payload = {
    user_id: userId,
    is_accepted: ourConditionsData.is_accepted,
  };

  try {
    const response = await saveConditionsAccept(payload);
    if (response?.status === 200) {
      iDbClearTable(INDEX_DB.OUR_CONDITIONS_TABLE);
    } else {
      responseMessage(RESPONSE_MESSAGES.ERROR);
    }
  } catch (error: any) {
    console.error(
      "An error occurred while submitting local our conditions data:",
      error,
    );
    throw error;
  }
};

/** Submit quiz data if available
 * @param options - Optional object containing callbacks and user data
 * @param options.userData - User data to merge with quiz payload (firstName, lastName, email)
 * @param options.onSuccess - Callback to be called on successful submission
 * @param options.onError - Callback to be called on error
 */
export const submitLocalQuizData = async (options?: {
  userData?: UserDataForQuizType;
  onSuccess?: () => Promise<void>;
  onError?: (error?: any) => void;
  onNeutral?: () => void;
}) => {
  const [userQuizData] = await iDbGetAllRecords<MenopauseAssessmentDataType>(
    INDEX_DB.QUESTIONNAIRES_TABLE,
  );
  if (!userQuizData) {
    options?.onNeutral?.();
    return;
  }
  try {
    // Merge user data with quiz data if provided
    const payload = options?.userData
      ? {
          ...userQuizData,
          firstName: options.userData.firstName || userQuizData.firstName,
          lastName: options.userData.lastName || userQuizData.lastName,
          email: options.userData.email || userQuizData.email,
        }
      : userQuizData;

    const quizSubmissionResponse = await submitQuizResponse(payload);
    if (quizSubmissionResponse?.status === 200) {
      // TODO: check the need of recoil state clearance here
      iDbClearTable(INDEX_DB.QUESTIONNAIRES_TABLE);
      await options?.onSuccess?.();
      // redirectTo(`${routes.profile.path}/${routes.results.path}`);
    } else {
      responseMessage(RESPONSE_MESSAGES.ERROR);
      options?.onError?.();
      redirectTo(`${routes.home.path}`);
      // window.location.href = `${import.meta.env.VITE_PUBLIC_URL}${routes.home.path}`;
    }
  } catch (error: any) {
    console.error("An error occurred while submitting Local quiz data:", error);
    options?.onError?.(error);
  }
};
